import { AfterViewChecked, AfterViewInit, Component, ElementRef, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, fromEvent, Observable, pairwise, throttleTime } from 'rxjs';
import { filter, map } from 'rxjs/operators';

@Component({
  selector: 'app-date-picker',
  imports: [CommonModule, TranslateModule],
  template: `
    <header class="mobile-bottom-sheet-header">
      <span class="mobile-bottom-sheet-header__cancel-btn" (click)="closeSheet()" translate>buttonComponent.Cancel</span>
      <h3 class="mobile-bottom-sheet-header__title" translate>Calendar</h3>
      <span class="mobile-bottom-sheet-header__action-btn" (click)="done()" translate>buttonComponent.done</span>
    </header>
    @if (!showYearSelection) {
      <div class="fixed-week-days">
        <div class="day" translate>mo</div>
        <div class="day" translate>tu</div>
        <div class="day" translate>we</div>
        <div class="day" translate>th</div>
        <div class="day" translate>fr</div>
        <div class="day" translate>sa</div>
        <div class="day" translate>su</div>
      </div>
      <div class="calendar-container" #calendarContainer [style.max-height.px]="maxHeight - fixedHeaderHeight">
        @for (month of months$ | async; track month) {
          <div class="month">
            <div (click)="toggleYearSelection()" class="month-header">{{ _getMonth(month) }}</div>
            <div class="date-grid">
              @for (day of _getMonthDays(month); track $index; let i = $index) {
                <div
                  class="date"
                  [class.empty]="day === 0"
                  [class.selected]="_isSelected(day, month)"
                  [class.weekend]="_isWeekend(day, month)"
                  [class.pre-selected]="isPreSelectedDay(day, month)"
                  [class.disabled]="(!pastDatesSelection && _isPastDate(day, month)) || isInPreselectedRange(day, month)"
                  (click)="_handleClick(day, month)"
                >
                  {{ day !== 0 ? day : '' }}
                </div>
              }
            </div>
          </div>
        }
      </div>
    } @else {
      <!-- Year selection view -->
      <div class="year-selection">
        <div class="year-selection__list">
          @for (y of years; track y) {
            <div class="year-selection__list__item" [id]="'year-' + y" (click)="selectYear(y)" [ngClass]="{ 'selected-year': y === selectedDate.getFullYear() }">
              {{ y }}
            </div>
          }
        </div>
      </div>
    }
  `,
  styleUrls: ['./date-picker.component.scss'],
})
/**
 * The DatePickerComponent is a component that displays a calendar with selectable dates. It is
 * used in the BottomSheetComponent to allow the user to select a date.
 * The DatePickerComponent has the following features:
 * - It displays a calendar with selectable dates.
 * - It allows the user to select a date.
 * - It allows the user to scroll through the months.
 * - It allows the user to select a date by clicking on it.
 */
export class DatePickerComponent implements OnInit, AfterViewChecked, AfterViewInit {
  @Input() pastDatesSelection = true;
  @Output() dateSelected = new EventEmitter<Date | string>();
  @ViewChild('calendarContainer') calendarContainer: ElementRef;
  selectedDate: Date = new Date();
  emittedDate: any;
  maxHeight = 750;
  months: Date[] = [];
  fixedHeaderHeight = 60;
  inputDate: string;
  startDateSelectionFrom: string | null = null;
  showYearSelection = false;
  years: number[] = [];
  // Observable for months
  months$: Observable<Date[]>;
  private readonly _bottomSheetRef = inject(NgxBottomSheetService);
  private readonly _translateService = inject(TranslateService);
  private readonly _monthsRange = new BehaviorSubject<{ startYear: number; endYear: number }>(null);
  private _hasScrolledToSelectedDate = false;

  /**
   * The ngOnInit function initializes the selectedDate variable based on the inputDate, which is
   * validated and parsed into a Date object.
   */
  ngOnInit(): void {
    this.inputDate = this._bottomSheetRef.currentSheetData.currentSelectedDate;
    this.pastDatesSelection = this._bottomSheetRef.currentSheetData.pastDatesSelection;
    this.startDateSelectionFrom = this._bottomSheetRef.currentSheetData.startDateSelectionFrom;

    if (this._validateDate(this.inputDate)) {
      // Handle different date formats
      if (this.inputDate.includes('-')) {
        // ISO 8601 format (YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS.sssZ)
        this.selectedDate = new Date(this.inputDate);
      } else if (this.inputDate.includes('/')) {
        // YYYY/MM/DD format
        const parts = this.inputDate.split('/');
        this.selectedDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      }
    } else {
      // Fallback to current date if the passed date is invalid
      this.selectedDate = new Date();
    }

    if (this.startDateSelectionFrom) {
      const startDate = new Date(this.startDateSelectionFrom);
      if (this.selectedDate < startDate) {
        this.selectedDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 1);
      }
    }

    // Initialize months$ observable based on monthsRange
    this.months$ = this._monthsRange.asObservable().pipe(
      map((range) => {
        const months = [];
        for (let year = range.startYear; year <= range.endYear; year++) {
          for (let month = 0; month < 12; month++) {
            months.push(new Date(year, month));
          }
        }
        return months;
      }),
    );

    this._generateYears();
    this._setInitialMonthsRange();
  }

  ngAfterViewInit(): void {
    this._setupScrollListener();
  }

  ngAfterViewChecked(): void {
    // Check if we've already scrolled to the selected date to avoid doing it more than once
    if (!this._hasScrolledToSelectedDate) {
      this._scrollToSelectedMonth();
      this._hasScrolledToSelectedDate = true; // Prevent future scrolls
    }
  }

  done() {
    this.closeSheet(this.emittedDate);
  }

  closeSheet(date?: any) {
    this._bottomSheetRef.close(date);
  }

  isInPreselectedRange(day: number, month: Date): boolean {
    if (!this.startDateSelectionFrom || day === 0) return false;

    const startDate = new Date(this.startDateSelectionFrom).setHours(0, 0, 0, 0);
    const currentDate = new Date(month.getFullYear(), month.getMonth(), day).setHours(0, 0, 0, 0);

    return currentDate < startDate;
  }

  isPreSelectedDay(day: number, month: Date): boolean {
    if (!this.startDateSelectionFrom || day === 0) return false;

    const startDate = new Date(this.startDateSelectionFrom).setHours(0, 0, 0, 0);
    const currentDate = new Date(month.getFullYear(), month.getMonth(), day).setHours(0, 0, 0, 0);
    return currentDate === startDate;
  }

  /**
   * The function `toggleYearSelection` toggles the year selection view.
   */
  toggleYearSelection(): void {
    this.showYearSelection = !this.showYearSelection;
    if (this.showYearSelection) {
      setTimeout(() => {
        const currentYearEl = document.getElementById('year-' + this.selectedDate.getFullYear());
        if (currentYearEl) {
          currentYearEl.scrollIntoView({ block: 'center', behavior: 'instant' });
        }
      }, 0);
    }
  }

  /**
   * The function `selectYear` selects a year from the year selection view and updates the selected date.
   * @param year
   */
  selectYear(year: number): void {
    const currentDate = this.selectedDate;
    this.selectedDate = new Date(year, currentDate.getMonth(), currentDate.getDate(), currentDate.getHours(), currentDate.getMinutes(), currentDate.getSeconds());
    this._setInitialMonthsRange();
    this.showYearSelection = false;
    setTimeout(() => {
      this._scrollToSelectedMonth();
    }, 0);
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // -------------------------------------------- Private Protected Functions --------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  /**
   * The handleClick function selects a date based on the day and month provided.
   * @param {number} day - The day parameter is a number representing the day of the month. It is used to
   * determine the selected day in the handleClick function.
   * @param {Date} month - The `month` parameter is of type `Date` and represents the month for which the
   * day is being clicked.
   */
  protected _handleClick(day: number, month: Date): void {
    if (day !== 0) {
      this._selectDate(new Date(month.getFullYear(), month.getMonth(), day));

      this.done();
    }
  }

  /**
   * The function checks if a given day and month is in the past.
   * @param {number} day - The `day` parameter is a number representing the day of the month. It is used
   * to create a new `Date` object with the specified day.
   * @param {Date} month - The `month` parameter is of type `Date` and represents the month for which we
   * want to check if a given day is in the past.
   * @returns a boolean value, indicating whether the given day and month is in the past or not.
   */
  protected _isPastDate(day: number, month: Date): boolean {
    if (day === 0) return false; // Skip empty days
    const dateToCheck = new Date(month.getFullYear(), month.getMonth(), day);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to compare only dates
    return dateToCheck < today;
  }

  /**
   * The function `getMonthDays` returns an array of numbers representing the days of the month, with the
   * start of the week adjusted to Monday.
   * @param {Date} month - The `month` parameter is a `Date` object representing the month for which you
   * want to get the days.
   * @returns an array of numbers representing the days of the month.
   */
  protected _getMonthDays(month: Date): number[] {
    const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    // Adjust the start of the week to Monday
    const dayOfWeek = (new Date(month.getFullYear(), month.getMonth(), 1).getDay() + 6) % 7;
    const daysArray = new Array(42).fill(0); // 42 = 7 days * 6 rows
    for (let i = 0; i < daysInMonth; i++) {
      // Place the day number in the correct position
      daysArray[i + dayOfWeek] = i + 1;
    }
    return daysArray;
  }

  /**
   * The function checks if a given date and month match the selected date.
   * @param {number} date - The `date` parameter is a number representing the day of the month.
   * @param {Date} month - The "month" parameter is of type "Date" and represents the month for which we
   * want to check if a specific date is selected.
   * @returns a boolean value.
   */
  protected _isSelected(date: number, month: Date): boolean {
    return date === this.selectedDate.getDate() && month.getMonth() === this.selectedDate.getMonth() && month.getFullYear() === this.selectedDate.getFullYear();
  }

  /**
   * The function determines if a given day in a month is a weekend or not.
   * @param {number} day - The `day` parameter is a number representing the day of the month. It can
   * range from 1 to 31.
   * @param {Date} month - The `month` parameter is a `Date` object representing a specific month.
   * @returns a boolean value indicating whether the given day is a weekend or not.
   */
  protected _isWeekend(day: number, month: Date): boolean {
    if (day === 0) return false; // If it's an empty cell, it's not a weekend
    const firstDayOfMonth = new Date(month.getFullYear(), month.getMonth(), 1).getDay();
    const dayOfWeek = (firstDayOfMonth + day - 1) % 7;
    return dayOfWeek === 6 || dayOfWeek === 0; // 0 = Sunday, 6 = Saturday (This is tricky, but it works)
  }

  /**
   * The function `_getMonth` returns the name of the month in the current locale.
   * @param month
   * @protected
   */
  protected _getMonth(month: Date): string {
    const translatedMonthName = this._translateService.instant(`${month.toLocaleString('default', { month: 'long' }).toLowerCase()}`);
    return `${translatedMonthName}, ${month.getFullYear()}`;
  }

  /**
   * The scrollToSelectedMonth function scrolls the calendar container to the selected month, centering
   * it within the container.
   * @private
   */
  private _scrollToSelectedMonth() {
    // The scrolling logic remains the same
    // Make sure this runs only after the months have been loaded and rendered
    const selectedMonthIndex = (this.selectedDate.getFullYear() - this._monthsRange.value.startYear) * 12 + this.selectedDate.getMonth();
    setTimeout(() => {
      const container = this.calendarContainer.nativeElement;
      const monthElements = container.querySelectorAll('.month');
      const selectedMonthElement = monthElements[selectedMonthIndex];
      if (selectedMonthElement) {
        container.scrollTop = selectedMonthElement.offsetTop - container.offsetTop;
      }
    }, 0); // Timeout ensures this runs after the current change detection cycle
  }

  /**
   * The function `validateDate` checks if a given string is a valid date in ISO 8601 format or
   * DD-MM-YYYY format or YYYY/MM/DD format.
   * @param {string} dateString - The `dateString` parameter is a string that represents a date. It can
   * be in one of the following formats:
   * @returns a boolean value. It returns true if the input dateString is in a valid date format (ISO
   * 8601 format or DD-MM-YYYY format or YYYY/MM/DD format), and false otherwise.
   */
  private _validateDate(dateString: string): boolean {
    // Check for ISO 8601 date format (e.g., 2024-12-27)
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const date = new Date(dateString);
      return date instanceof Date && !isNaN(date.getTime());
    }

    // Check for ISO 8601 datetime format (e.g., 2024-12-27T19:00:00.000Z)
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/.test(dateString)) {
      const date = new Date(dateString);
      return date instanceof Date && !isNaN(date.getTime());
    }

    // Check for DD-MM-YYYY format (e.g., 27-12-2024) or YYYY/MM/DD format (e.g., 2024/12/27)
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString) || /^\d{4}\/\d{2}\/\d{2}$/.test(dateString)) {
      const parts = dateString.split(/[-/]/); // Split on both '-' and '/'
      const date = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
      return date instanceof Date && !isNaN(date.getTime());
    }

    return false;
  }

  /**
   * The function extends the range of months by adding future years if the direction is 'next', or by
   * adding previous years if the direction is 'previous' and past dates selection is enabled.
   * @param {'previous' | 'next'} direction - The `direction` parameter is a string that can have two
   * possible values: 'previous' or 'next'. It is used to determine whether to extend the months range in
   * the previous direction (by adding previous years) or in the next direction (by adding future years).
   */
  private _extendMonthsRange(direction: 'previous' | 'next') {
    const currentRange = this._monthsRange.value;
    if (currentRange) {
      if (direction === 'next') {
        // Extend the range by adding future years
        const newEndYear = currentRange.endYear + 1;
        this._monthsRange.next({ ...currentRange, endYear: newEndYear });
      } else if (this.pastDatesSelection && direction === 'previous') {
        // Extend the range by adding previous years
        const newStartYear = currentRange.startYear - 1;
        this._monthsRange.next({ startYear: newStartYear, endYear: currentRange.endYear });
      }
    }
  }

  /**
   * The function `_setInitialMonthsRange` sets a range of years based on the selected date, with a
   * buffer if the selected year is outside the current range.
   */
  private _setInitialMonthsRange() {
    // Assuming this.selectedDate has already been set from inputDate
    const selectedYear = this.selectedDate.getFullYear();
    const currentYear = new Date().getFullYear();

    // Check if selected year is outside the current range
    let startYear, endYear;
    if (selectedYear < currentYear - 1 || selectedYear > currentYear + 1) {
      // Adjust the range to include the selected year with some buffer
      startYear = selectedYear;
      endYear = selectedYear + 1;
    } else {
      // Default range around the current year
      startYear = currentYear;
      endYear = currentYear + 1;
    }

    this._monthsRange.next({ startYear, endYear });
  }

  /**
   * The selectDate function takes a date as input, sets the time to noon to avoid DST changes, converts
   * it to the format 'yyyy-mm-dd', updates the selectedDate property, emits the selected date, and
   * closes the sheet.
   * @param {Date} date - The `date` parameter is a `Date` object representing the selected date.
   */

  private _selectDate(date: Date): void {
    // Create a new Date object with the time set to noon to avoid any DST changes that might affect the date
    const selectedDateWithNoon = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0);

    // Convert to ISO string and then to the format 'yyyy-mm-dd'
    const emittedDate = selectedDateWithNoon.toISOString().split('T')[0];

    // Update the selectedDate with the date that has the noon time to keep consistency
    this.selectedDate = selectedDateWithNoon;
    this.emittedDate = emittedDate;
    this.dateSelected.emit(emittedDate);
  }

  /**
   * The function sets up a scroll listener on a container element and extends the range of months
   * displayed based on the scroll direction.
   */
  private _setupScrollListener() {
    const container = this.calendarContainer.nativeElement;
    fromEvent(container, 'scroll')
      .pipe(
        throttleTime(100),
        map(() => container.scrollTop),
        pairwise(), // Use pairwise to compare previous and current scroll positions
        filter(([prev, curr]) => this._shouldLoadMoreMonths(prev, curr, container.scrollHeight, container.clientHeight)),
      )
      .subscribe({
        next: ([prev, curr]) => {
          const direction = curr > prev ? 'next' : 'previous';
          this._extendMonthsRange(direction);
        },
      });
  }

  /**
   * The function shouldLoadMoreMonths determines whether to load more months based on the scroll
   * direction and position of the user on the page.
   * @param {any} prevScrollTop - The previous scroll position in pixels.
   * @param {number} currScrollTop - The current scroll position from the top of the container.
   * @param {number} scrollHeight - The total height of the scrollable content, including any overflow.
   * @param {any} clientHeight - The clientHeight parameter represents the height of the visible portion
   * of the scrollable element.
   * @returns a boolean value.
   */
  private _shouldLoadMoreMonths(prevScrollTop: any, currScrollTop: number, scrollHeight: number, clientHeight: any): boolean {
    // determine whether to load more months based on scroll direction and position
    const atBottom = currScrollTop + clientHeight >= scrollHeight - 100; // Near bottom
    const atTop = currScrollTop <= 100; // Near top
    return atBottom || atTop;
  }

  private _generateYears(): void {
    const currentYear = new Date().getFullYear();
    const years: number[] = [];
    for (let y = 1900; y <= currentYear + 10; y++) {
      years.push(y);
    }
    this.years = years;
  }
}
